"use client";

import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Gages de confiance affichés sous le pitch. Repris tels quels de la page
// accélérateur (Tosa RS6962, Qualiopi, format 3 mois + mentor).
const ITEMS = [
    {
        icon: "medal-ribbon-star-bold-duotone",
        titleKey: "PythonTosa_item_tosa_title",
        descKey: "PythonTosa_item_tosa_desc",
    },
    {
        icon: "shield-check-bold-duotone",
        titleKey: "PythonTosa_item_qualiopi_title",
        descKey: "PythonTosa_item_qualiopi_desc",
    },
    {
        icon: "user-speak-rounded-bold-duotone",
        titleKey: "PythonTosa_item_format_title",
        descKey: "PythonTosa_item_format_desc",
    },
];

export default function PythonTosaCertification() {
    const t = useTranslations();

    return (
        <section id="python-certification" className="pt-16">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10 p-8 sm:p-10"
            >
                {/* Glow discret */}
                <div className="absolute -top-20 -right-16 w-[360px] h-[360px] bg-sky-500/15 rounded-full blur-3xl pointer-events-none" />

                <div className="relative">
                    {/* Eyebrow */}
                    <div className="inline-flex items-center gap-2 rounded-full bg-sky-500/15 ring-1 ring-sky-400/30 px-3 py-1 mb-4">
                        <SolarIcon
                            name="diploma-verified-bold-duotone"
                            className="w-3.5 h-3.5 text-sky-300"
                        />
                        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-sky-300">
                            {t("PythonTosa_eyebrow")}
                        </span>
                    </div>

                    {/* Titre */}
                    <h3 className="text-2xl sm:text-3xl font-bold text-white leading-tight tracking-tight mb-3 max-w-[640px] font-sourceSans">
                        {t("PythonTosa_title")}
                    </h3>

                    {/* Différenciation certificat / certification */}
                    <p className="text-[15px] sm:text-base text-gray-300/85 leading-relaxed mb-7 max-w-[640px] font-geistSans">
                        {t("PythonTosa_desc")}
                    </p>

                    {/* Gages de confiance */}
                    <div className="grid sm:grid-cols-3 gap-4 mb-8">
                        {ITEMS.map((item) => (
                            <div
                                key={item.titleKey}
                                className="flex items-start gap-3 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-4"
                            >
                                <div className="shrink-0 w-10 h-10 rounded-xl bg-sky-500/15 ring-1 ring-sky-400/30 flex items-center justify-center">
                                    <SolarIcon
                                        name={item.icon}
                                        className="w-5 h-5 text-sky-300"
                                    />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[14px] font-semibold text-white leading-tight mb-0.5">
                                        {t(item.titleKey)}
                                    </p>
                                    <p className="text-[12.5px] text-gray-400 leading-snug mb-0">
                                        {t(item.descKey)}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* CTA */}
                    <Link
                        href="/accelerators/python"
                        className="btn-landing inline-flex items-center gap-2 rounded-full bg-sky-500 hover:bg-sky-400 px-6 py-3 text-sm font-semibold text-white transition-colors"
                    >
                        {t("PythonTosa_cta")}
                        <SolarIcon name="arrow-right-bold" className="w-4 h-4" />
                    </Link>
                </div>
            </motion.div>
        </section>
    );
}
